import type { GetStaticProps } from 'next'
import type { FC, FormEvent } from 'react'
import type { Category } from '@/types'

import { useState } from 'react'

import { Seo } from '@/components/generals/seo'
import { SiteLayout } from '@/components/layout/site-layout'
import { GoldCta } from '@/components/products'

import { getLayoutData } from '@/lib/layout-data'

interface ContactProps {
    categories: Category[]
}

interface MapTab {
    id: string
    title: string
    address: string
    image: string
    srcSet: string
}

const MAP_TABS: MapTab[] = [
    {
        id: 'Tab 1',
        title: 'New York',
        address: '1901 Thornridge Cir. Shiloh, Hawaii 81063',
        image: '/images/Google-Map-Screnshot-02.webp',
        srcSet: '/images/Google-Map-Screnshot-02-p-500.jpg 500w, /images/Google-Map-Screnshot-02-p-800.jpg 800w, /images/Google-Map-Screnshot-02-p-1080.jpg 1080w, /images/Google-Map-Screnshot-02.webp 1612w'
    },
    {
        id: 'Tab 2',
        title: 'San Francisco',
        address: '2715 Ash Dr. San Jose, South Dakota 83475',
        image: '/images/Google-Map-Screnshot-01.webp',
        srcSet: '/images/Google-Map-Screnshot-01-p-500.jpg 500w, /images/Google-Map-Screnshot-01-p-800.jpg 800w, /images/Google-Map-Screnshot-01-p-1080.jpg 1080w, /images/Google-Map-Screnshot-01.webp 1619w'
    },
    {
        id: 'Tab 3',
        title: 'Chicago',
        address: '2118 Thornridge Cir. Syracuse, Connecticut 35624',
        image: '/images/Google-Map-Screnshot-03.jpg',
        srcSet: '/images/Google-Map-Screnshot-03-p-500.jpg 500w, /images/Google-Map-Screnshot-03-p-800.jpg 800w, /images/Google-Map-Screnshot-03-p-1080.jpg 1080w, /images/Google-Map-Screnshot-03.jpg 1620w'
    }
]

const DESCRIPTION =
    "Get in touch with Milano. Send us a message or find one of our head offices in New York, San Francisco, or Chicago."

const Contact: FC<ContactProps> = ({ categories }) => {
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [message, setMessage] = useState('')
    const [submitted, setSubmitted] = useState(false)
    const [activeTab, setActiveTab] = useState('Tab 1')

    const onSubmit = (event: FormEvent) => {
        event.preventDefault()
        if (!name.trim() || !email.trim() || !message.trim()) return
        setSubmitted(true)
    }

    return (
        <SiteLayout categories={categories}>
            <Seo title='Contact us' description={DESCRIPTION} />

            <div className='section relative'>
                <div className='container relative'>
                    <div className='contact-hero-section-holder'>
                        <div className='w-layout-grid grid-6-col'>
                            <div id='w-node-cf43cb81-3d12-55df-0613-6a090667bd20-6e6eeb54' className='content'>
                                <div className='animation-on-load-01'>
                                    <h2>Contact us</h2>
                                    <div className='send-message-container'>
                                        <div className='animation-on-load-01'>
                                            <h3 className='accent-text-color'>Send us a message</h3>
                                        </div>
                                    </div>
                                </div>
                                <div className='animation-on-load-02'>
                                    <div className='w-form'>
                                        {submitted ? (
                                            <div className='success-message black w-form-done' style={{ display: 'block' }}>
                                                <div>Thank you! Your submission has been received!</div>
                                            </div>
                                        ) : (
                                            <form name='wf-form-Contact-Form' method='get' onSubmit={onSubmit}>
                                                <div className='w-layout-grid form-grid'>
                                                    <div id='w-node-edb90237-725d-4abb-8b66-6ddd8632e28d-6e6eeb54' className='fields-container'>
                                                        <label htmlFor='First-Name' className='contact-field-label'>Name</label>
                                                        <input
                                                            className='contact-text-field w-input'
                                                            maxLength={256}
                                                            name='First-Name'
                                                            placeholder=''
                                                            type='text'
                                                            id='First-Name'
                                                            required
                                                            value={name}
                                                            onChange={(event) => setName(event.target.value)}
                                                        />
                                                    </div>
                                                    <div id='w-node-_636179ea-09d4-1363-2b36-31136978fb80-6e6eeb54' className='fields-container'>
                                                        <label htmlFor='email' className='contact-field-label'>e-mail</label>
                                                        <input
                                                            className='contact-text-field w-input'
                                                            maxLength={256}
                                                            name='email'
                                                            placeholder=''
                                                            type='email'
                                                            id='email'
                                                            required
                                                            value={email}
                                                            onChange={(event) => setEmail(event.target.value)}
                                                        />
                                                    </div>
                                                    <div id='w-node-c3eb9be0-c7fe-4771-fbc0-63144788750e-6e6eeb54' className='fields-container last'>
                                                        <label htmlFor='Message' className='contact-field-label'>Message</label>
                                                        <textarea
                                                            id='Message'
                                                            name='Message'
                                                            maxLength={5000}
                                                            placeholder=''
                                                            required
                                                            className='contact-text-field message w-input'
                                                            value={message}
                                                            onChange={(event) => setMessage(event.target.value)}
                                                        ></textarea>
                                                    </div>
                                                </div>
                                                <input type='submit' className='button full w-button' value='Submit' />
                                            </form>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='contact-hero-section-image'>
                    <img
                        src='/images/Contact-Hero-Image-1.jpg'
                        loading='lazy'
                        srcSet='/images/Contact-Hero-Image-1-p-500.jpg 500w, /images/Contact-Hero-Image-1-p-800.jpg 800w, /images/Contact-Hero-Image-1.jpg 1270w'
                        sizes='(max-width: 991px) 100vw, 43vw'
                        alt=''
                        className='contact-hero-image'
                    />
                </div>
            </div>

            <div className='section map-section'>
                <div className='container _6ems'>
                    <div className='w-layout-grid grid-6-col'>
                        <div id='w-node-_1910c8a7-0faf-f430-8b57-a6df0a58eda1-6e6eeb54' className='content'>
                            <div className='fade-in-move-on-scroll'>
                                <h3 className='accent-text-color'>Our Head Offices</h3>
                            </div>
                            <div className='fade-in-move-on-scroll'>
                                <p className='paragraph-m grey-text'>Lorem ipsum dolor sit amet, consectetur adipiscing elit.</p>
                            </div>
                        </div>
                    </div>
                    <div className='tabs w-tabs'>
                        <div className='tabs-menu w-tab-menu'>
                            {MAP_TABS.map((tab) => (
                                <a
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`tab-link w-inline-block w-tab-link${activeTab === tab.id ? ' w--current' : ''}`}
                                >
                                    <div className='tab-link-content'>
                                        <h4>{tab.title}</h4>
                                        <p className='paragraph-m grey-text'>{tab.address}</p>
                                    </div>
                                </a>
                            ))}
                        </div>
                        <div className='tabs-content w-tab-content'>
                            {MAP_TABS.map((tab) => (
                                <div
                                    key={tab.id}
                                    className={`tab-pane w-tab-pane${activeTab === tab.id ? ' w--tab-active' : ''}`}
                                >
                                    <div className='map w-widget w-widget-map' role='region'></div>
                                    <img
                                        srcSet={tab.srcSet}
                                        alt=''
                                        src={tab.image}
                                        loading='lazy'
                                        sizes='(max-width: 479px) 94vw, (max-width: 1439px) 54vw, 52vw'
                                        className='map-image'
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className='section'>
                <div className='divider'></div>
                <div className='container _6ems'>
                    <div className='w-layout-grid grid-6-col'>
                        <div id='w-node-_7df055d6-7b42-0b62-d328-1b169960294e-6e6eeb54' className='contact-card-container slide-in-bottom'>
                            <div className='contact-icon-container'>
                                <img src='/images/Email-Icon---Gold.svg' loading='lazy' alt='' className='_100width' />
                            </div>
                            <h4>E-mail</h4>
                            <p className='paragraph-m grey-text'>Lorem ipsum dolor sit amet, consectetur adipiscing elit.</p>
                            <a href='mailto:milano@address.com' className='simple-link'>milano@address.com</a>
                        </div>
                        <div id='w-node-_6a85f28c-3bfc-c6b5-8b14-1fb4ad4b1024-6e6eeb54' className='contact-card-container slide-in-bottom'>
                            <div className='contact-icon-container'>
                                <img src='/images/Phone-Icon.svg' loading='lazy' alt='' className='_100width' />
                            </div>
                            <h4>Phone</h4>
                            <p className='paragraph-m grey-text'>Lorem ipsum dolor sit amet, consectetur adipiscing elit.</p>
                            <a href='tel:+383(0)49123456' className='simple-link'>+383 (0) 49 123 456</a>
                        </div>
                        <div id='w-node-_2d7d490f-4061-a8fa-3c30-01f1454bf81f-6e6eeb54' className='contact-card-container slide-in-bottom'>
                            <div className='contact-icon-container'>
                                <img src='/images/Map-Pin-Golden-Icon.svg' loading='lazy' alt='' className='_100width' />
                            </div>
                            <h4>Our Head Office</h4>
                            <p className='paragraph-m grey-text'>Lorem ipsum dolor sit amet, consectetur adipiscing elit.</p>
                            <a href='https://maps.google.com/' target='_blank' className='simple-link'>2972 Westheimer Rd. Santa Ana, Illinois 85486</a>
                        </div>
                    </div>
                </div>
            </div>

            <GoldCta />
        </SiteLayout>
    )
}

export const getStaticProps: GetStaticProps<ContactProps> = async () => {
    const { categories } = getLayoutData()

    return {
        props: {
            categories
        }
    }
}

export default Contact
